import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Hammer,
  Plus,
  Trash2,
  Layers,
  Loader2,
  PenLine,
  KanbanSquare,
  Layout as LayoutIcon,
  MonitorSmartphone,
  BarChart3,
  ShoppingBag,
  Sparkles,
} from "lucide-react";

const ICON_MAP = {
  PenLine,
  KanbanSquare,
  Layout: LayoutIcon,
  MonitorSmartphone,
  BarChart3,
  ShoppingBag,
};

export default function AppForge() {
  const [templates, setTemplates] = useState([]);
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("templates");

  const loadData = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        api.get("/forge/templates"),
        api.get("/forge/creations"),
      ]);
      setTemplates(t.data || []);
      setCreations(c.data || []);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreateDialog = (template) => {
    setSelectedTemplate(template);
    setName(template.name + " copy");
    setDescription(template.description || "");
    setError("");
    setDialogOpen(true);
  };

  const submitCreation = async () => {
    if (!selectedTemplate) return;
    setCreating(true);
    setError("");
    try {
      const { data } = await api.post("/forge/creations", {
        name: name.trim(),
        description: description.trim(),
        template_id: selectedTemplate.id,
      });
      setCreations((prev) => [data, ...prev]);
      setDialogOpen(false);
      setActiveTab("creations");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const deleteCreation = async (id) => {
    try {
      await api.delete(`/forge/creations/${id}`);
      setCreations((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      // ignore
    }
  };

  const toggleStatus = async (creation) => {
    const next = creation.status === "draft" ? "published" : "draft";
    try {
      const { data } = await api.patch(`/forge/creations/${creation.id}`, {
        status: next,
      });
      setCreations((prev) => prev.map((c) => (c.id === creation.id ? data : c)));
    } catch (e) {
      // ignore
    }
  };

  return (
    <Layout title="App Forge" subtitle="custom-app-forge-14">
      <section className="mb-8 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-indigo-700 mb-2">
            Module · 01
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
            Forge new apps from ready-made templates.
          </h2>
          <p className="mt-3 text-sm text-slate-600 max-w-2xl leading-relaxed">
            Pick a template, name your new creation, and start iterating. Every
            creation lives in this workspace beside your other modules.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2">
          <Hammer className="h-4 w-4 text-indigo-700" />
          <span className="font-mono text-[11px] uppercase tracking-widest text-indigo-700">
            {creations.length} creation{creations.length === 1 ? "" : "s"}
          </span>
        </div>
      </section>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6 bg-slate-100 p-1 h-auto" data-testid="forge-tabs">
          <TabsTrigger value="templates" data-testid="forge-tab-templates" className="text-sm">
            <Layers className="mr-2 h-4 w-4" /> Templates
          </TabsTrigger>
          <TabsTrigger value="creations" data-testid="forge-tab-creations" className="text-sm">
            <Hammer className="mr-2 h-4 w-4" /> My creations
          </TabsTrigger>
        </TabsList>

        <TabsContent value="templates">
          {loading ? (
            <LoadingState />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {templates.map((tpl) => {
                const Icon = ICON_MAP[tpl.icon] || Sparkles;
                return (
                  <div
                    key={tpl.id}
                    data-testid={`template-card-${tpl.id}`}
                    className="rounded-md border border-slate-200 bg-white p-6 hover:border-slate-300 transition-all duration-150 flex flex-col"
                  >
                    <div className="flex items-start justify-between">
                      <div className="h-10 w-10 rounded-md border border-indigo-200 bg-indigo-50 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-indigo-700" strokeWidth={1.75} />
                      </div>
                      <span className="font-mono text-[10px] tracking-widest uppercase text-slate-500">
                        {tpl.complexity}
                      </span>
                    </div>
                    <div className="mt-4">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                        {tpl.category}
                      </div>
                      <div className="font-display text-lg font-semibold text-slate-900">
                        {tpl.name}
                      </div>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                        {tpl.description}
                      </p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-slate-200">
                      <Button
                        size="sm"
                        onClick={() => openCreateDialog(tpl)}
                        className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                        data-testid={`template-forge-button-${tpl.id}`}
                      >
                        <Plus className="mr-1.5 h-3.5 w-3.5" /> Forge from template
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="creations">
          {loading ? (
            <LoadingState />
          ) : creations.length === 0 ? (
            <div
              className="rounded-md border border-dashed border-slate-300 bg-white p-12 text-center"
              data-testid="forge-empty-state"
            >
              <div className="mx-auto h-12 w-12 rounded-md border border-indigo-200 bg-indigo-50 flex items-center justify-center mb-4">
                <Hammer className="h-5 w-5 text-indigo-700" />
              </div>
              <div className="font-display text-xl font-semibold text-slate-900">
                No creations yet
              </div>
              <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto">
                Pick a template from the gallery to forge your first app.
              </p>
              <Button
                onClick={() => setActiveTab("templates")}
                className="mt-5 bg-slate-900 hover:bg-slate-800"
                data-testid="forge-goto-templates"
              >
                Browse templates
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {creations.map((c) => {
                const Icon = ICON_MAP[c.template_icon] || Sparkles;
                return (
                  <div
                    key={c.id}
                    data-testid={`creation-card-${c.id}`}
                    className="rounded-md border border-slate-200 bg-white p-6 flex flex-col"
                  >
                    <div className="flex items-start justify-between">
                      <div className="h-9 w-9 rounded-md border border-indigo-200 bg-indigo-50 flex items-center justify-center">
                        <Icon className="h-4 w-4 text-indigo-700" strokeWidth={1.75} />
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleStatus(c)}
                        data-testid={`creation-status-toggle-${c.id}`}
                        className={`font-mono text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm border ${
                          c.status === "published"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-slate-200 bg-slate-50 text-slate-600"
                        }`}
                      >
                        {c.status || "draft"}
                      </button>
                    </div>
                    <div className="mt-4 flex-1">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">
                        {c.template_name}
                      </div>
                      <div className="font-display text-lg font-semibold text-slate-900">
                        {c.name}
                      </div>
                      {c.description && (
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                          {c.description}
                        </p>
                      )}
                    </div>
                    <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-between">
                      <span className="font-mono text-[10px] text-slate-500">
                        {new Date(c.created_at).toLocaleDateString()}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteCreation(c.id)}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                        data-testid={`creation-delete-${c.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="forge-create-dialog">
          <DialogHeader>
            <DialogTitle>Forge new creation</DialogTitle>
            <DialogDescription>
              Based on{" "}
              <span className="font-medium text-slate-900">
                {selectedTemplate?.name}
              </span>
              .
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="forge-name">Name</Label>
              <Input
                id="forge-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My new app"
                data-testid="forge-create-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="forge-description">Description</Label>
              <Textarea
                id="forge-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                data-testid="forge-create-description-input"
              />
            </div>
            {error && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="forge-create-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={submitCreation}
              disabled={creating || !name.trim()}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="forge-create-submit"
            >
              {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function LoadingState() {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
    </div>
  );
}
